import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Calendar } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    website: '' // Honeypot field
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Scroll to form if hash is present
  useEffect(() => {
    if (window.location.hash === '#contact-form') {
      setTimeout(() => {
        const formElement = document.getElementById('contact-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Please enter your name';
    if (!formData.email.trim()) return 'Please enter your email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.service) return 'Please select a service';
    if (!formData.message.trim()) return 'Please enter a message';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.website) {
      console.log('Bot detected via honeypot');
      // Silently fail for bots - don't show error
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate('/thank-you');
      }, 1000);
      return;
    }
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source_page: window.location.pathname
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Success - navigate to thank you page
      navigate('/thank-you');
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const services = [
    { value: '', label: 'Select a service' },
    { value: 'ASD Assessment', label: 'ASD Assessment' },
    { value: 'ADHD Assessment', label: 'ADHD Assessment' },
    { value: 'Therapy Services', label: 'Therapy Services' },
    { value: 'General Enquiry', label: 'General Enquiry' },
  ];

  return (
    <>
      <SEO
        title="Contact Us - Book Your Assessment or Therapy Session"
        description="Contact Navigating Minds Psychology to book an ASD assessment, ADHD assessment, or therapy session. We're here to help across Australia via telehealth."
        keywords="contact psychologist, book assessment, book therapy, psychology enquiry, ASD assessment booking, ADHD assessment booking"
        url="/contact"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Get in <span style={{ color: 'var(--blue-primary)' }}>Touch</span>
              </h1>
              <p className="text-lg md:text-xl" style={{ color: 'var(--text-secondary)' }}>
                Ready to take the first step? We're here to help.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Contact Form Section - Primary */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <FadeInUp>
              <div id="contact-form" className="bg-white p-8 md:p-12 rounded-2xl shadow-xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                  Send us a Message
                </h2>
                <p className="text-center text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
                  We'll respond within 1-2 business days
                </p>
                
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-5">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--blue-primary)] focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--blue-primary)] focus:border-transparent"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Phone (optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone-input"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--blue-primary)] focus:border-transparent"
                        placeholder="0400 000 000"
                      />
                    </div>

                    {/* Service */}
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Service *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        data-testid="contact-service-select"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--blue-primary)] focus:border-transparent"
                      >
                        {services.map((service) => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Honeypot Field - Hidden from humans, visible to bots */}
                    <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                      <label htmlFor="website">Website (leave blank)</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="contact-message-textarea"
                        rows="5"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--blue-primary)] focus:border-transparent resize-none"
                        placeholder="Tell us about your needs..."
                      ></textarea>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="contact-error-message">
                        <p className="text-red-600 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      className="w-full px-8 py-4 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--navy-primary)] transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Contact Information Section - Secondary */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Other Ways to Reach Us
                </h2>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  Prefer to call or email? We're here to help.
                </p>
              </div>
            </FadeInUp>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <FadeInUp delay={0.1}>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    <Phone size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Phone</h3>
                  <a href="tel:+61721503636" className="text-lg hover:text-[var(--blue-primary)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
                    07 2150 3636
                  </a>
                </div>
              </FadeInUp>

              <FadeInUp delay={0.2}>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    <Mail size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Email</h3>
                  <a href="mailto:admin@navigatingminds.com.au" className="text-lg hover:text-[var(--blue-primary)] transition-colors break-all" style={{ color: 'var(--text-secondary)' }}>
                    admin@navigatingminds.com.au
                  </a>
                </div>
              </FadeInUp>

              <FadeInUp delay={0.3}>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    <MapPin size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Service Area</h3>
                  <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                    All of Australia
                  </p>
                </div>
              </FadeInUp>
            </div>

            {/* Calendar Widget */}
            <FadeInUp delay={0.4}>
              <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-[var(--blue-primary)]">
                <div className="text-center mb-4">
                  <Calendar className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--blue-primary)' }} />
                  <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    Book Your Appointment
                  </h3>
                  <p className="text-base mt-2" style={{ color: 'var(--text-secondary)' }}>
                    Select a time that works for you
                  </p>
                </div>
                <div className="overflow-hidden rounded-xl">
                  <iframe 
                    src="https://www.halaxy.com/book/widget/psychologist/robyn-maddison/1426371/1109601" 
                    style={{ border: 0, width: '100%', height: '1000px' }}
                    title="Book Appointment with Robyn Maddison"
                  />
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;